import React from 'react';
import {
  Alert, Button, ConfirmDialog, EmptyState, ErrorState, FilterChip, PageHeader, Pager, SearchInput,
  SkeletonRow, Table, useToast,
} from '@parsina/framework';
import { ExternalLink, Globe, SearchX, X } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import { useShortcut } from '../../lib/shortcuts';
import { EMPTY } from '../../lib/format';
import { DOCTORSITE_LABEL } from '../../lib/runtimeEnv';
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_CHARS } from '../../constants/list';
import { DOCTOR_PAGES_QUERY, RESTORE_DOCTOR_PAGE, SUSPEND_DOCTOR_PAGE } from './queries';
import {
  DOCTOR_PAGE_SIZE, PAGE_STATUSES, PageStatusBadge, actionFor,
} from './shared';
import SuspendPageDialog from './SuspendPageDialog';

/** The URL contract (DESIGN_CONCEPT §3.2). Defaults are absent from the address. */
const SCHEMA = {
  status: { default: '', values: ['', ...PAGE_STATUSES] },
  q: { default: '' },
  page: { default: 1, parse: (raw) => Number(raw) || 1 },
};

/** The server call's variables for a URL view. A 1-char search narrows nothing. */
export function pagesVariables(view, pageSize = DOCTOR_PAGE_SIZE) {
  const q = String(view?.q || '').trim();
  const page = Math.max(1, Number(view?.page) || 1);
  return {
    status: view?.status || '',
    query: q.length >= SEARCH_MIN_CHARS ? q.slice(0, 120) : '',
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };
}

/**
 * «صفحات پزشکان» — the platform's takedown surface over every doctor's public
 * page at parsinabot.ir/doctors/<slug> (docs/DOCTOR_PUBLIC_PAGE_DESIGN.md §D5,
 * §8.8). Archetype B (Report/List):
 *
 *   ① PageHeader · ④ toolbar (status chips + search) · ⑤ table · ⑥ pager
 *
 * Publishing is free (no pre-publication review), so this screen exists to
 * TAKE DOWN: «تعلیق» asks for a reason the doctor is shown, «بازگردانی» lifts
 * it to «لغو انتشار» and leaves re-publishing to the doctor. Paging and the
 * filters are server-side (`doctorPages(status, query, limit, offset)`), because
 * every practitioner has a page by default and the estate is the size of the
 * practitioner registry.
 *
 * After either write the BFF asks the doctor site to revalidate the page; the
 * list simply refetches (dimmed, never blanked).
 */
export default function DoctorPages() {
  const { t, formatDate, formatNumber } = useTranslation();
  const toast = useToast();
  useDocumentTitle('nav.title.doctorPages');

  const [view, setView, resetView] = useUrlState(SCHEMA);
  const variables = pagesVariables(view);
  const key = JSON.stringify(variables);

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const loaded = React.useRef(false);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    return gql(DOCTOR_PAGES_QUERY, JSON.parse(key))
      .then((d) => { setData(d?.doctorPages || { pages: [], total: 0 }); loaded.current = true; })
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [key, t]);

  // A filter or page change after the first load dims the table rather than
  // replacing it with skeletons, so the operator keeps their place.
  React.useEffect(() => { load({ quiet: loaded.current }); }, [load]);

  // ── the two writes ──────────────────────────────────────────────────────
  const [suspending, setSuspending] = React.useState(null);   // the row
  const [restoring, setRestoring] = React.useState(null);     // the row
  const [pending, setPending] = React.useState(false);
  const [dialogError, setDialogError] = React.useState(null);

  const openSuspend = (row) => { setDialogError(null); setSuspending(row); };
  const openRestore = (row) => { setDialogError(null); setRestoring(row); };

  /** §2.4: pending → success closes + toasts + refetches | failure stays open inline. */
  const run = async (document, vars, successKey, close) => {
    setPending(true);
    setDialogError(null);
    try {
      await gql(document, vars);
      close();
      toast.success(t(successKey));
      load({ quiet: true });
    } catch (e) {
      setDialogError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const pages = data?.pages || [];
  const total = Number(data?.total || 0);
  const pageCount = Math.max(1, Math.ceil(total / DOCTOR_PAGE_SIZE));
  const isFiltered = !!(view.status || String(view.q || '').trim().length >= SEARCH_MIN_CHARS);

  const columns = [
    {
      key: 'name',
      header: t('doctorPages.columns.name'),
      render: (r) => (
        <div className="min-w-0">
          {/* A name is DATA: rendered in the script it was typed in. */}
          <div dir="auto" className="truncate font-semibold text-foreground" title={r.displayName || ''}>
            {r.displayName || t('doctorPages.unnamed')}
          </div>
          {r.specialty ? (
            <div dir="auto" className="truncate text-xs text-muted-foreground" title={r.specialty}>{r.specialty}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'council',
      header: t('doctorPages.columns.council'),
      width: '8rem',
      render: (r) => (r.councilNumber
        ? <span {...LTR_TEXT} className="font-mono text-xs">{r.councilNumber}</span>
        : EMPTY),
    },
    {
      key: 'slug',
      header: t('doctorPages.columns.slug'),
      width: '14rem',
      render: (r) => <PublicLink row={r} />,
    },
    {
      key: 'status',
      header: t('doctorPages.columns.status'),
      width: '8rem',
      render: (r) => <PageStatusBadge status={r.status} data-testid={`doctor-pages-status-${r.pageId}`} />,
    },
    {
      key: 'publishedAt',
      header: t('doctorPages.columns.publishedAt'),
      width: '8rem',
      render: (r) => (r.publishedAt ? formatDate(r.publishedAt) : EMPTY),
    },
    {
      key: 'reason',
      header: t('doctorPages.columns.reason'),
      width: '14rem',
      render: (r) => {
        if (!r.suspendedReason) return EMPTY;
        const by = r.suspendedBy || r.suspendedAt
          ? t('doctorPages.suspendedBy', {
            actor: r.suspendedBy || EMPTY,
            date: r.suspendedAt ? formatDate(r.suspendedAt) : EMPTY,
          })
          : '';
        return (
          <div
            dir="auto"
            className="truncate text-sm"
            title={by ? `${r.suspendedReason} — ${by}` : r.suspendedReason}
            data-testid={`doctor-pages-reason-${r.pageId}`}
          >
            {r.suspendedReason}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: t('doctorPages.columns.actions'),
      width: '8rem',
      render: (r) => {
        const action = actionFor(r.status);
        const name = r.displayName || r.slug;
        if (action === 'suspend') {
          return (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive"
              onClick={() => openSuspend(r)}
              aria-label={t('doctorPages.actions.suspendAria', { name })}
              data-testid={`doctor-pages-suspend-${r.pageId}`}
            >
              {t('doctorPages.actions.suspend')}
            </Button>
          );
        }
        if (action === 'restore') {
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openRestore(r)}
              aria-label={t('doctorPages.actions.restoreAria', { name })}
              data-testid={`doctor-pages-restore-${r.pageId}`}
            >
              {t('doctorPages.actions.restore')}
            </Button>
          );
        }
        return <span className="text-muted-foreground">{t('doctorPages.actions.none')}</span>;
      },
    },
  ];

  return (
    <div className="flex h-full flex-col bg-background" data-testid="doctor-pages">
      <PageHeader title={t('doctorPages.title')} sub={t('doctorPages.sub', { site: DOCTORSITE_LABEL })} />

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <Toolbar
          view={view}
          onChange={setView}
          onClear={resetView}
          isFiltered={isFiltered}
          resultLabel={!loading && !error ? t('doctorPages.count', { count: total }) : ''}
        />

        <div
          className="rounded-lg border border-border bg-card transition-opacity"
          style={{ opacity: refreshing ? 0.45 : 1 }}
          aria-busy={refreshing || undefined}
        >
          {loading ? (
            <div
              data-testid="doctor-pages-loading"
              data-state="loading"
              role="status"
              aria-busy="true"
              aria-label={t('doctorPages.loading')}
            >
              <table className="w-full">
                <tbody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <SkeletonRow key={i} cols={7} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : error ? (
            <ErrorState
              title={t('doctorPages.error')}
              description={error}
              onRetry={() => load()}
              retryLabel={t('common.actions.retry')}
              data-testid="doctor-pages-error"
            />
          ) : (
            <div data-state={pages.length ? 'content' : 'empty'}>
              <Table
                dense
                ariaLabel={t('doctorPages.tableAria')}
                columns={columns}
                rows={pages}
                rowKey={(r) => r.pageId}
                rowProps={(r) => ({ 'data-testid': `doctor-pages-row-${r.pageId}` })}
                empty={isFiltered ? (
                  <EmptyState
                    icon={<SearchX size={44} strokeWidth={1.5} aria-hidden />}
                    title={t('doctorPages.emptyFiltered.title')}
                    description={t('doctorPages.emptyFiltered.desc')}
                    action={(
                      <Button variant="outline" onClick={resetView}>
                        {t('common.actions.clearFilters')}
                      </Button>
                    )}
                    data-testid="doctor-pages-empty"
                  />
                ) : (
                  <EmptyState
                    icon={<Globe size={44} strokeWidth={1.5} aria-hidden />}
                    title={t('doctorPages.empty.title')}
                    description={t('doctorPages.empty.desc')}
                    data-testid="doctor-pages-empty"
                  />
                )}
                data-testid="doctor-pages-table"
              />
            </div>
          )}
        </div>

        {!loading && !error && pageCount > 1 ? (
          <div data-testid="doctor-pages-pager">
            <Pager
              page={Math.min(Math.max(1, view.page), pageCount)}
              pageCount={pageCount}
              pageSize={DOCTOR_PAGE_SIZE}
              onChange={(page) => setView({ page })}
              prevLabel={t('common.pager.prev')}
              nextLabel={t('common.pager.next')}
              pageLabel={(page, count) => t('common.pager.label', { page, total: count })}
              formatNumber={formatNumber}
            />
          </div>
        ) : null}
      </div>

      <SuspendPageDialog
        page={suspending}
        pending={pending}
        error={dialogError}
        onOpenChange={(open) => { if (!open && !pending) setSuspending(null); }}
        onSubmit={({ reason }) => run(
          SUSPEND_DOCTOR_PAGE,
          { pageId: suspending.pageId, reason },
          'doctorPages.suspend.success',
          () => setSuspending(null),
        )}
      />

      {restoring ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => { if (!open && !pending) setRestoring(null); }}
          title={t('doctorPages.restore.title', { name: restoring.displayName || restoring.slug })}
          description={t('doctorPages.restore.body')}
          confirmLabel={t('doctorPages.restore.submit')}
          cancelLabel={t('common.actions.cancel')}
          loading={pending}
          onConfirm={() => run(
            RESTORE_DOCTOR_PAGE,
            { pageId: restoring.pageId },
            'doctorPages.restore.success',
            () => setRestoring(null),
          )}
          data-testid="doctor-pages-restore"
          confirmTestId="doctor-pages-restore-confirm"
          cancelTestId="doctor-pages-restore-cancel"
        >
          {dialogError ? (
            <Alert variant="error" data-testid="doctor-pages-restore-error">{dialogError}</Alert>
          ) : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * The slug, and — only while the page is actually public — a link to it. A
 * link to a suspended or unpublished page would open the site's 404, which
 * reads as "the page is broken" rather than "the page is down on purpose".
 */
function PublicLink({ row }) {
  const { t } = useTranslation();
  const slug = <span {...LTR_TEXT} className="truncate font-mono text-xs">{row.slug}</span>;
  if (row.status !== 'published' || !row.publicUrl) {
    return <span className="inline-flex min-w-0 items-center" title={t('doctorPages.notPublic')}>{slug}</span>;
  }
  return (
    <a
      href={row.publicUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline"
      aria-label={t('doctorPages.openPublic', { slug: row.slug })}
      title={row.publicUrl}
      data-testid={`doctor-pages-link-${row.pageId}`}
    >
      {slug}
      <ExternalLink size={13} aria-hidden className="shrink-0" />
    </a>
  );
}

/**
 * Zone ④. Status chips apply at once; the search echoes instantly and writes
 * the URL after the debounce (DESIGN_CONCEPT §8, §9.2). `F3` and `/` focus it
 * through the shortcut registry.
 */
function Toolbar({
  view, onChange, onClear, isFiltered, resultLabel,
}) {
  const { t } = useTranslation();
  const searchRef = React.useRef(null);
  const [draft, setDraft] = React.useState(view.q || '');

  React.useEffect(() => { setDraft(view.q || ''); }, [view.q]);
  React.useEffect(() => {
    if (draft === (view.q || '')) return undefined;
    const timer = setTimeout(() => onChange({ q: draft, page: 1 }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, view.q, onChange]);

  const focusSearch = () => { searchRef.current?.focus(); searchRef.current?.select?.(); };
  useShortcut('f3', focusSearch, { label: t('shortcuts.action.search'), group: t('shortcuts.groups.doctorPages') });
  useShortcut('/', focusSearch, { label: t('shortcuts.action.search'), group: t('shortcuts.groups.doctorPages') });

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3" data-testid="doctor-pages-toolbar">
      <div role="group" aria-label={t('doctorPages.filters.label')} className="flex flex-wrap gap-1.5" data-testid="doctor-pages-filter">
        {['', ...PAGE_STATUSES].map((s) => (
          <FilterChip
            key={s || 'all'}
            label={s ? t(`doctorPages.status.${s}`) : t('doctorPages.filters.all')}
            active={(view.status || '') === s}
            onClick={() => onChange({ status: s, page: 1 })}
            data-testid={`doctor-pages-filter-${s || 'all'}`}
          />
        ))}
      </div>
      <div className="min-w-[16rem] flex-1">
        <SearchInput
          ref={searchRef}
          value={draft}
          onChange={(e) => setDraft(e.target?.value ?? e)}
          onKeyDown={(e) => { if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); } }}
          placeholder={t('doctorPages.search.placeholder')}
          aria-label={t('doctorPages.search.label')}
          data-testid="doctor-pages-search"
        />
      </div>
      {resultLabel ? <span className="text-sm text-muted-foreground" aria-live="polite">{resultLabel}</span> : null}
      {isFiltered ? (
        <Button variant="ghost" size="sm" onClick={onClear} data-testid="doctor-pages-clear">
          <X size={14} aria-hidden />
          {t('common.actions.clearFilters')}
        </Button>
      ) : null}
    </div>
  );
}
